import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, KanbanSquare, Link2Off, Lock, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITY_COLOURS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { ProjectGuideCard } from '@/features/projects';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { DueChip } from '@/components/shared/TaskRow';
import { ApiError, api } from '@/lib/api';
import { cn, formatMinutes } from '@/lib/utils';
import { useUsers } from '@/features/settings/api';
import { useRoom } from '@/hooks/useRealtime';
import { UserLink } from '@/components/shared/UserLink';
import { useProject, type ProjectStatusView } from '@/features/projects';
import { CANNOT_MOVE, useBoardSensors, useCanMoveTask } from './boardDnd';
import { FilterBar, useFilters } from './FilterBar';
import { useTasks, type TaskView } from './api';

interface BoardColumn {
  statusId: string;
  count: number;
  wipLimit: number | null;
  over: boolean;
}

function TaskCard({
  task,
  people,
  onOpen,
  dragging,
  locked,
}: {
  task: TaskView;
  people: Record<string, { name: string; avatarUrl?: string }>;
  onOpen?: () => void;
  dragging?: boolean;
  locked?: boolean;
}) {
  const checklistDone = task.checklist.filter((c) => c.done).length;
  const steps = task.subtaskCount + task.checklist.length;
  const doneSteps = task.subtaskDoneCount + checklistDone;

  return (
    <article
      className={cn(
        'vt-surface group relative select-none py-2 pl-3 pr-2',
        locked ? 'cursor-default' : 'cursor-grab active:cursor-grabbing',
        dragging && 'opacity-40',
      )}
      onDoubleClick={onOpen}
    >
      <span className="vt-spine" style={{ background: PRIORITY_COLOURS[task.priority] }} aria-hidden />
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          onClick={onOpen}
          className="vt-mono shrink-0 text-[10px] vt-muted hover:text-[var(--accent)]"
        >
          {task.key}
        </button>
        {task.blockedBy.length > 0 && (
          <span title={task.blockedReason ?? 'Blocked'} className="text-[var(--danger)]">
            <Link2Off className="size-3" />
          </span>
        )}
        {locked && (
          // Subtle until hovered: on a board where the viewer can move very
          // little, a permanent row of padlocks is noise rather than a signal.
          <span
            className="ml-auto opacity-0 transition-opacity group-hover:opacity-60"
            title="You can only move tasks assigned to you"
            data-testid="card-lock"
          >
            <Lock className="size-3" />
          </span>
        )}
      </div>

      <p className="mt-0.5 line-clamp-3 text-[13px] leading-snug">{task.title}</p>

      <div className="mt-2 flex items-center gap-2">
        <DueChip dueDate={task.dueDate} done={task.statusCategory === 'done'} />
        {steps > 0 && (
          <span className="vt-mono text-[10px] vt-muted">
            {doneSteps}/{steps}
          </span>
        )}
        {task.commentCount > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] vt-muted">
            <MessageSquare className="size-2.5" />
            {task.commentCount}
          </span>
        )}
        {task.estimateMinutes ? (
          <span className="vt-mono text-[10px] vt-muted">{formatMinutes(task.estimateMinutes)}</span>
        ) : null}

        <span className="ml-auto flex -space-x-1.5">
          {/* V5-B0: the avatar opens that person's profile. `UserLink` stops
              propagation, so this never starts a drag or opens the task. */}
          {task.assigneeIds.slice(0, 3).map((id) => (
            <UserLink
              key={id}
              userId={id}
              name={people[id]?.name ?? 'Unknown'}
              avatarUrl={people[id]?.avatarUrl}
              avatarOnly
              size={18}
              className="ring-1 ring-[var(--surface)]"
            />
          ))}
        </span>
      </div>

      {steps > 0 && (
        <div className="mt-1.5 h-[3px] w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div
            className="h-full rounded-full bg-[var(--accent)]"
            style={{ width: `${Math.round((doneSteps / steps) * 100)}%` }}
          />
        </div>
      )}
    </article>
  );
}

function SortableCard(
  props: Parameters<typeof TaskCard>[0] & { canMove: boolean },
) {
  const { canMove, ...cardProps } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task._id,
    disabled: !canMove,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      data-testid={canMove ? undefined : 'locked-card'}
      // A drag that silently does nothing is what reads as "the board is
      // broken", so a card the viewer cannot move says so on hover and when
      // they try (V5-02 §2.3).
      title={canMove ? undefined : CANNOT_MOVE}
      onPointerDownCapture={
        canMove
          ? undefined
          : () => toast.error(CANNOT_MOVE, { id: 'board-locked' })
      }
      {...attributes}
      {...listeners}
    >
      <TaskCard {...cardProps} dragging={isDragging} locked={!canMove} />
    </div>
  );
}

function Column({
  status,
  tasks,
  meta,
  people,
  collapsed,
  onToggle,
  onOpen,
}: {
  status: ProjectStatusView;
  tasks: TaskView[];
  meta?: BoardColumn;
  people: Record<string, { name: string; avatarUrl?: string }>;
  collapsed: boolean;
  onToggle: () => void;
  onOpen: (task: TaskView) => void;
}) {
  const over = meta?.over ?? false;

  /**
   * The column itself is the drop target — V5-02, and the actual cause of
   * "I cannot drag a task from To do to In progress".
   *
   * Only the cards were ever registered with dnd-kit, so `over` could only be
   * another card. Dropping anywhere else in a column resolved to nothing, and
   * an empty column could not be dropped into at all. `onDragEnd` already
   * handled `over.id` being a status id — that branch was simply unreachable
   * because no droppable ever carried one.
   */
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({ id: status.id });
  const canMove = useCanMoveTask();

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="flex w-9 shrink-0 flex-col items-center gap-2 rounded-[6px] bg-[var(--surface-2)] py-2"
        aria-label={`Expand ${status.name}`}
      >
        <ChevronRight className="size-3.5" />
        <span className="vt-mono text-[10px]">{meta?.count ?? tasks.length}</span>
        <span className="[writing-mode:vertical-rl] text-[11px] vt-muted">{status.name}</span>
      </button>
    );
  }

  return (
    <section
      ref={setDroppableRef}
      className={cn(
        'flex w-[272px] shrink-0 flex-col rounded-[6px] bg-[var(--surface-2)]',
        isOver && 'outline outline-2 outline-[var(--accent)]',
      )}
      data-testid="board-column"
      data-status-id={status.id}
      data-status-name={status.name}
      data-over={isOver ? 'true' : undefined}
    >
      <header
        className="flex items-center gap-1.5 px-2 py-1.5"
        style={{ color: over ? 'var(--danger)' : undefined }}
      >
        <button type="button" onClick={onToggle} aria-label={`Collapse ${status.name}`}>
          <ChevronDown className="size-3.5" />
        </button>
        <span className="size-2 rounded-full" style={{ background: status.colour }} aria-hidden />
        <h2 className="truncate text-[12px] font-medium">{status.name}</h2>
        <span className="vt-mono ml-auto text-[11px]">
          {meta?.count ?? tasks.length}
          {status.wipLimit ? ` / ${status.wipLimit}` : ''}
        </span>
      </header>

      <SortableContext items={tasks.map((t) => t._id)} strategy={verticalListSortingStrategy}>
        <div className="flex min-h-[60px] flex-col gap-1.5 overflow-y-auto px-1.5 pb-2" data-status={status.id}>
          {tasks.map((task) => (
            <SortableCard
              key={task._id}
              task={task}
              people={people}
              canMove={canMove(task)}
              onOpen={() => onOpen(task)}
            />
          ))}
          {tasks.length === 0 && (
            <p className="px-2 py-4 text-center text-[11px] vt-muted">Nothing here</p>
          )}
        </div>
      </SortableContext>
    </section>
  );
}

export function BoardView() {
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const filters = useFilters();
  const { data: project, isLoading: projectLoading } = useProject(key);
  const { data: people = [] } = useUsers();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);

  const query = useMemo(
    () => ({ ...filters.toQuery(), projectId: project?._id, limit: 200, includeSubtasks: true }),
    [filters, project],
  );
  const { data, isLoading } = useTasks(query, { enabled: Boolean(project) });

  const { data: columns } = useQuery({
    queryKey: ['board-columns', project?._id],
    queryFn: async () => (await api.get<BoardColumn[]>(`/tasks/${project!._id}/board-columns`)).data,
    enabled: Boolean(project),
  });

  const move = useMutation({
    mutationFn: async (input: {
      id: string;
      statusId?: string;
      afterTaskId?: string | null;
      beforeTaskId?: string | null;
    }) => {
      // `id` addresses the task in the URL and must NOT also appear in the
      // body: `moveTaskSchema` is `.strict()`, so the extra key made the server
      // reject the request outright.
      //
      // This was the bug. Every drag on every board answered
      // `400 VALIDATION_ERROR: Unrecognized key(s) in object: 'id'`, so no move
      // ever persisted. It went unnoticed because the failure was silent —
      // there was no optimistic update to snap back, so the card simply never
      // left its column, which looks exactly like a drag that did not register.
      const { id, ...payload } = input;
      return (await api.patch<TaskView>(`/tasks/${id}/move`, payload)).data;
    },

    /**
     * Move the card now, ask the server after (V5-02 §2.1).
     *
     * Without this the card sat in its old column until a request and a refetch
     * had both completed — which on a slow connection is indistinguishable from
     * a drag that did not work, and is most of why this feature "felt" broken
     * even where it functioned.
     */
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const snapshot = qc.getQueriesData({ queryKey: ['tasks'] });

      if (input.statusId) {
        qc.setQueriesData<{ items: TaskView[] }>({ queryKey: ['tasks'] }, (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((t) =>
              t._id === input.id ? { ...t, statusId: input.statusId! } : t,
            ),
          };
        });
      }

      return { snapshot };
    },

    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: ['board-columns'] });
    },

    onError: (err: unknown, _input, context) => {
      // Snap back to exactly what was on screen before, then say why. Restoring
      // the snapshot rather than refetching means the card returns immediately
      // instead of hovering in the wrong column until the network answers.
      for (const [key, data] of context?.snapshot ?? []) qc.setQueryData(key, data);
      void qc.invalidateQueries({ queryKey: ['tasks'] });

      if (err instanceof ApiError && err.code === 'WIP_LIMIT_EXCEEDED') {
        toast.error(err.message, { description: 'Raise the limit or turn enforcement off in project settings.' });
      } else if (err instanceof ApiError && err.status === 403) {
        // V5-02 §2.3 — the permission case, said out loud rather than as a
        // drag that quietly does nothing.
        toast.error(err.message || CANNOT_MOVE);
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Could not move the card');
      }
    },
  });

  // Task changes are broadcast to `project:<id>`, never workspace-wide, so a
  // viewer has to ask for them. The board never did — which is why a card
  // another person moved only appeared here after a manual refresh, despite
  // `task:moved` being wired up on the client the whole time (V5-02 §2.1).
  useRoom(project ? `project:${project._id}` : undefined);

  /** Column ids in display order — the axis keyboard left/right steps along. */
  const orderedStatusIds = useMemo(
    () =>
      (project?.statuses ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((s) => s.id),
    [project],
  );

  const sensors = useBoardSensors(orderedStatusIds);

  const tasks = (data?.items ?? []) as TaskView[];
  const peopleById = useMemo(
    () => Object.fromEntries(people.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [people],
  );

  const byStatus = useMemo(() => {
    const map = new Map<string, TaskView[]>();
    for (const status of project?.statuses ?? []) map.set(status.id, []);
    for (const task of tasks) {
      const list = map.get(task.statusId);
      if (list) list.push(task);
    }
    for (const list of map.values()) list.sort((a, b) => (a.rank < b.rank ? -1 : 1));
    return map;
  }, [tasks, project]);

  const columnMeta = useMemo(
    () => new Map((columns ?? []).map((c) => [c.statusId, c])),
    [columns],
  );

  function onDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const moved = tasks.find((t) => t._id === String(active.id));
    if (!moved) return;

    const overTask = tasks.find((t) => t._id === String(over.id));
    const targetStatusId = overTask?.statusId ?? String(over.id);
    const column = byStatus.get(targetStatusId) ?? [];

    if (overTask) {
      const index = column.findIndex((t) => t._id === overTask._id);
      const movingDown = moved.statusId === targetStatusId && moved.rank < overTask.rank;
      const afterTaskId = movingDown ? overTask._id : (column[index - 1]?._id ?? null);
      const beforeTaskId = movingDown ? (column[index + 1]?._id ?? null) : overTask._id;
      move.mutate({
        id: moved._id,
        statusId: targetStatusId !== moved.statusId ? targetStatusId : undefined,
        afterTaskId,
        beforeTaskId,
      });
      return;
    }

    if (targetStatusId !== moved.statusId) {
      move.mutate({ id: moved._id, statusId: targetStatusId });
    }
  }

  if (projectLoading) {
    return (
      <div className="p-4">
        <Skeleton className="h-[60vh]" />
      </div>
    );
  }

  const activeTask = activeId ? tasks.find((t) => t._id === activeId) : null;

  return (
    <>
      <PageHeader
        title={`${project?.icon ?? ''} ${project?.name ?? 'Board'}`}
        subtitle={`${tasks.length} visible`}
        actions={
          <Button variant="secondary" size="sm" onClick={() => navigate(`/projects/${key}/list`)}>
            List view
          </Button>
        }
        toolbar={<FilterBar showGrouping={false} />}
      />

      <div className="p-4">
        {/* V3 R4 — visible until the guide is written, then gone. */}
        <div className="mb-3">
          <ProjectGuideCard projectId={project?._id} />
        </div>

        {isLoading ? (
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[50vh] w-[272px]" />
            ))}
          </div>
        ) : (project?.statuses ?? []).length === 0 ? (
          <EmptyState
            icon={<KanbanSquare className="size-6" />}
            title="This project has no statuses"
            description="Add a pipeline in project settings to start using the board."
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          >
            <div className="flex gap-3 overflow-x-auto pb-2">
              {(project?.statuses ?? [])
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((status) => (
                  <Column
                    key={status.id}
                    status={status}
                    tasks={byStatus.get(status.id) ?? []}
                    meta={columnMeta.get(status.id)}
                    people={peopleById}
                    collapsed={collapsed.has(status.id)}
                    onToggle={() =>
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        if (next.has(status.id)) next.delete(status.id);
                        else next.add(status.id);
                        return next;
                      })
                    }
                    onOpen={(task) => navigate(`/tasks/${task.key}`)}
                  />
                ))}
            </div>

            <DragOverlay>
              {activeTask ? <TaskCard task={activeTask} people={peopleById} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>
    </>
  );
}
